import { useEffect, useRef, useState, useCallback } from 'react';

export const useCrossfadeLoop = (url: string, overlap: number = 2) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [needsUserInteraction, setNeedsUserInteraction] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const nextPlayTimeRef = useRef<number>(0);
  const schedulerTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sourceNodesRef = useRef<{ source: AudioBufferSourceNode; gain: GainNode }[]>([]);
  const autoPlayAttemptedRef = useRef(false);
  const isInitializingRef = useRef(false); // 防止重复初始化
  const schedulerRunningRef = useRef(false); // 防止重复调度
  
  // Load Audio and attempt auto-play
  useEffect(() => {
    // 防止重复初始化（React Strict Mode 可能导致双重执行）
    if (isInitializingRef.current) {
      return;
    }
    isInitializingRef.current = true;

    const loadAudio = async () => {
      // Check if AudioContext is available (not available in SSR or some browsers)
      if (typeof window === 'undefined' || (!window.AudioContext && !(window as any).webkitAudioContext)) {
        console.error("[Music] AudioContext is not available");
        isInitializingRef.current = false;
        return;
      }

      try {
        // 清理旧的 AudioContext（如果存在）
        if (audioContextRef.current) {
          try {
            // 停止所有正在播放的节点
            sourceNodesRef.current.forEach(({ source, gain }) => {
              try {
                const currTime = audioContextRef.current!.currentTime;
                gain.gain.cancelScheduledValues(currTime);
                gain.gain.setValueAtTime(0, currTime);
                source.stop(currTime);
              } catch (e) {
                // ignore
              }
            });
            sourceNodesRef.current = [];
            
            // 清理调度器
            if (schedulerTimerRef.current) {
              clearTimeout(schedulerTimerRef.current);
              schedulerTimerRef.current = null;
            }
            
            // 关闭旧的 AudioContext
            await audioContextRef.current.close();
          } catch (e) {
            // ignore cleanup errors
          }
          audioContextRef.current = null;
        }

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;
        
        const decodedBuffer = await audioContext.decodeAudioData(arrayBuffer);
        audioBufferRef.current = decodedBuffer;
        setIsLoaded(true);

        // Try to auto-play (only once, but always check state)
        const shouldAttemptAutoPlay = !autoPlayAttemptedRef.current;
        if (shouldAttemptAutoPlay) {
          autoPlayAttemptedRef.current = true;
        }
        
        // Always check state and try to resume if needed
        if (audioContext.state === 'suspended') {
          if (shouldAttemptAutoPlay) {
            try {
              await audioContext.resume();
            } catch (resumeError) {
              // Resume failed, will be handled by state check below
            }
          }
        }
        
        // Wait for state to settle
        await new Promise(resolve => setTimeout(resolve, 150));
        const finalState = audioContext.state;
        
        // Check final state and set needsUserInteraction accordingly
        if (finalState === 'running') {
          if (shouldAttemptAutoPlay) {
            // 确保在设置播放状态前，重置调度器状态
            schedulerRunningRef.current = false;
            nextPlayTimeRef.current = audioContext.currentTime;
            setIsPlaying(true);
          }
        } else {
          setNeedsUserInteraction(true);
        }
      } catch (error) {
        console.error("[Music] Failed to load audio:", error);
      } finally {
        isInitializingRef.current = false;
      }
    };

    loadAudio();

    return () => {
      // 清理函数：停止播放并关闭 AudioContext
      if (schedulerTimerRef.current) {
        clearTimeout(schedulerTimerRef.current);
        schedulerTimerRef.current = null;
      }
      
      // 停止所有正在播放的节点
      sourceNodesRef.current.forEach(({ source, gain }) => {
        try {
          if (audioContextRef.current) {
            const currTime = audioContextRef.current.currentTime;
            gain.gain.cancelScheduledValues(currTime);
            gain.gain.setValueAtTime(0, currTime);
            source.stop(currTime);
          }
        } catch (e) {
          // ignore
        }
      });
      sourceNodesRef.current = [];
      
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          // ignore cleanup errors
        });
        audioContextRef.current = null;
      }
      
      schedulerRunningRef.current = false;
      isInitializingRef.current = false;
    };
  }, [url]);

  const playSnippet = useCallback((startTime: number) => {
    const ctx = audioContextRef.current;
    const buffer = audioBufferRef.current;
    if (!ctx || !buffer) return;

    // Create nodes
    const source = ctx.createBufferSource();
    const gain = ctx.createGain();
    
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(ctx.destination);

    // Fade In
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(1, startTime + overlap);

    // Fade Out
    const endTime = startTime + buffer.duration;
    const fadeOutStart = endTime - overlap;
    gain.gain.setValueAtTime(1, fadeOutStart);
    gain.gain.linearRampToValueAtTime(0, endTime);

    // Start and Stop
    source.start(startTime);
    source.stop(endTime);

    // Keep track to clean up later
    const nodeEntry = { source, gain };
    sourceNodesRef.current.push(nodeEntry);
    
    // Cleanup when done
    source.onended = () => {
      const index = sourceNodesRef.current.indexOf(nodeEntry);
      if (index > -1) {
        sourceNodesRef.current.splice(index, 1);
      }
    };
  }, [overlap]);

  const schedule = useCallback(() => {
    const ctx = audioContextRef.current;
    const buffer = audioBufferRef.current;
    
    // 防止重复调度：如果调度器已经在运行，直接返回
    if (!ctx || !buffer || !isPlaying || schedulerRunningRef.current) {
      return;
    }
    
    schedulerRunningRef.current = true;

    // Lookahead: 0.1s
    const lookahead = 0.1;

    // While next scheduled time is within lookahead window
    while (nextPlayTimeRef.current < ctx.currentTime + lookahead) {
      playSnippet(nextPlayTimeRef.current);
      nextPlayTimeRef.current += (buffer.duration - overlap);
    }

    schedulerTimerRef.current = setTimeout(() => {
      schedulerRunningRef.current = false;
      if (isPlaying && ctx && buffer) {
        schedule();
      }
    }, 25);
  }, [isPlaying, overlap, playSnippet]);

  const enablePlayback = useCallback(async () => {
    const ctx = audioContextRef.current;
    if (!ctx || !isLoaded) {
      return false;
    }

    try {
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      // Verify context is running
      if (ctx.state === 'running') {
        setNeedsUserInteraction(false);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("[Music] Failed to enable playback:", error);
      return false;
    }
  }, [isLoaded]);

  const toggle = useCallback(async () => {
    const ctx = audioContextRef.current;
    if (!ctx || !isLoaded) {
      return;
    }

    // If needs user interaction, enable playback first
    if (needsUserInteraction) {
      const enabled = await enablePlayback();
      if (!enabled) return;
    }

    if (isPlaying) {
      // Stop
      setIsPlaying(false);
      schedulerRunningRef.current = false; // 标记调度器已停止
      
      if (schedulerTimerRef.current) {
        clearTimeout(schedulerTimerRef.current);
        schedulerTimerRef.current = null;
      }
      
      // Stop all currently playing nodes
      sourceNodesRef.current.forEach(({ source, gain }) => {
        try {
            // Fade out immediately to avoid click
            const currTime = ctx.currentTime;
            gain.gain.cancelScheduledValues(currTime);
            gain.gain.setValueAtTime(gain.gain.value, currTime);
            gain.gain.linearRampToValueAtTime(0, currTime + 0.5);
            source.stop(currTime + 0.5);
        } catch (e) {
            // ignore
        }
      });
      sourceNodesRef.current = [];
    } else {
      // Start
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      // 重置调度器状态和播放时间
      schedulerRunningRef.current = false;
      nextPlayTimeRef.current = ctx.currentTime;
      setIsPlaying(true);
      // schedule 会在 useEffect 中自动调用
    }
  }, [isPlaying, isLoaded, needsUserInteraction, enablePlayback]);

  // Watch isPlaying to start/stop scheduler
  useEffect(() => {
    if (isPlaying) {
      // 确保调度器没有在运行，然后启动
      if (!schedulerRunningRef.current) {
        schedule();
      }
    } else {
      // 停止调度器
      schedulerRunningRef.current = false;
      if (schedulerTimerRef.current) {
        clearTimeout(schedulerTimerRef.current);
        schedulerTimerRef.current = null;
      }
    }
  }, [isPlaying, schedule]);

  return { isPlaying, toggle, isLoaded, needsUserInteraction, enablePlayback };
};

